import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { usePortalAuth } from "@/context/PortalAuth";
import { BrandMark } from "@/components/BrandMark";
import { LayoutDashboard, FileText, ScrollText, MessageSquare, ClipboardList, LogOut, Menu, X, UserCog, CalendarDays } from "lucide-react";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";

export default function PortalLayout({ children, title, subtitle, actions }) {
  const { slug } = useParams();
  const { me, logout } = usePortalAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [isCommittee, setIsCommittee] = useState(false);

  useEffect(() => {
    if (!me?.person) return;
    api.get("/portal/committee/context").then(({ data }) => setIsCommittee(!!data.is_committee)).catch(() => setIsCommittee(false));
  }, [me?.person?.id]);

  const isTenant = (me?.person?.roles || []).includes("Tenant");
  const items = [
    { to: `/portal/${slug}`, icon: LayoutDashboard, label: "Overview" },
    !isTenant && { to: `/portal/${slug}/invoices`, icon: FileText, label: "Invoices" },
    { to: `/portal/${slug}/notices`, icon: ScrollText, label: "Notices" },
    { to: `/portal/${slug}/complaints`, icon: MessageSquare, label: "Complaints" },
    !isTenant && { to: `/portal/${slug}/applications`, icon: ClipboardList, label: "Applications" },
    !isTenant && { to: `/portal/${slug}/facilities`, icon: CalendarDays, label: "Facilities" },
    isCommittee && { to: `/portal/${slug}/committee`, icon: UserCog, label: "Committee", pill: "New" },
  ].filter(Boolean);

  return (
    <div className="min-h-screen flex bg-sand-50 text-ink">
      {/* Mobile menu */}
      {open && (
        <div className="fixed inset-0 z-40 bg-ink/30" onClick={() => setOpen(false)}>
          <aside className="absolute left-0 top-0 bottom-0 w-72 bg-sand-200 border-r border-stone-200 p-6 space-y-2" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <BrandMark variant="portal" size={36} />
              <button onClick={() => setOpen(false)} className="p-2"><X size={20}/></button>
            </div>
            {items.map((it) => (
              <Link key={it.to} to={it.to} onClick={() => setOpen(false)}
                className="flex items-center gap-3 px-4 py-3 rounded-lg text-base font-medium text-stone-700 hover:bg-white/60">
                <it.icon size={18} strokeWidth={1.5}/> {it.label}
              </Link>
            ))}
          </aside>
        </div>
      )}

      <aside className="hidden lg:flex flex-col w-64 bg-sand-200 border-r border-stone-200">
        <div className="px-6 py-6"><Link to={`/portal/${slug}`}><BrandMark variant="portal" size={40} /></Link></div>
        <div className="px-6 pb-4">
          <div className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Society</div>
          <div className="editorial text-lg mt-0.5 leading-tight">{me?.society?.name}</div>
        </div>
        <nav className="px-3 flex-1">
          {items.map((it) => {
            const active = it.to === `/portal/${slug}` ? loc.pathname === it.to : loc.pathname.startsWith(it.to);
            return (
              <Link key={it.to} to={it.to} data-testid={`nav-${it.label.toLowerCase()}`}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg text-[15px] font-medium transition-colors ${active ? "side-active text-ink" : "text-stone-600 hover:text-ink"}`}>
                <it.icon size={18} strokeWidth={1.5}/> <span className="flex-1">{it.label}</span>
                {it.pill && <span className="text-[10px] rounded-full bg-terracotta/10 text-terracotta font-semibold px-2 py-0.5 uppercase tracking-widest">{it.pill}</span>}
              </Link>
            );
          })}
        </nav>
        <div className="p-4 border-t border-stone-300/60">
          <div className="text-xs text-stone-500">Signed in as</div>
          <div className="text-sm font-medium mt-0.5">{me?.person?.name || me?.person?.email || me?.person?.phone}</div>
          <button onClick={async () => { await logout(); nav(`/portal/${slug}/login`); }}
            data-testid="portal-logout"
            className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-full border border-stone-300 bg-white hover:bg-sand-100 px-4 py-2 text-sm">
            <LogOut size={14}/> Log out
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <header className="border-b border-stone-200 bg-sand-50 sticky top-0 z-10">
          <div className="max-w-5xl mx-auto px-6 lg:px-10 py-5 flex items-center justify-between gap-4">
            <button className="lg:hidden p-2" onClick={() => setOpen(true)} data-testid="menu-open"><Menu size={22}/></button>
            <div className="flex-1">
              {subtitle && <div className="text-xs uppercase tracking-widest text-stone-500 font-semibold mb-1">{subtitle}</div>}
              <h1 className="editorial text-2xl lg:text-3xl leading-tight" data-testid="page-title">{title}</h1>
            </div>
            <div>{actions}</div>
          </div>
        </header>
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-8">{children}</div>
      </main>
    </div>
  );
}
